import { format } from 'date-fns/format';
import { validateEmail } from '@bloomays-lib/utils.shared';
import type { PrimesBreadcrumbContext } from '../stores/breadcrumbStore';
import { buildCLevelAnnualDetailPath } from '../utils/annualPrime.utils';
import {
  formatMonthYearToMonthLabel,
  formatReferenceDateToMonthYear,
  getDefaultPrimeReferenceDate,
  isValidMonthYear,
  parseMonthYearToDate,
} from '../utils/monthlyPrime.utils';

export type BreadcrumbCrumb = {
  label: string;
  to?: string;
};

type BuildPrimesBreadcrumbTrailParams = {
  pathname: string;
  searchParams: URLSearchParams;
  isCLevel: boolean;
  primesContext: PrimesBreadcrumbContext | null;
  translateMenu: (key: string, options?: Record<string, string>) => string;
};

/**
 * Construit le libellé lisible d'une mission pour le fil d'Ariane
 * (ex: "CTO - Loïc Calvy chez Hermès"). Retourne `undefined` faute de label exploitable.
 */
export const buildMissionBreadcrumbLabel = ({
  label,
  bloomerFullName,
  clientName,
}: {
  label?: string | null;
  bloomerFullName?: string | null;
  clientName?: string | null;
}): string | undefined => {
  if (!label) return undefined;
  const base = [label, bloomerFullName].filter(Boolean).join(' - ');
  return clientName ? `${base} chez ${clientName}` : base;
};

export const buildPrimesBreadcrumbTrail = ({
  pathname,
  searchParams,
  isCLevel,
  primesContext,
  translateMenu,
}: BuildPrimesBreadcrumbTrailParams): BreadcrumbCrumb[] | null => {
  if (!pathname.startsWith('/primes')) {
    return null;
  }

  const primesLabel = translateMenu('primes');
  const crumbs: BreadcrumbCrumb[] = [{ label: primesLabel }];

  if (pathname === '/primes/dashboard') {
    crumbs.push({ label: translateMenu('breadcrumb.tamSynthesis') });
    return crumbs;
  }

  if (pathname === '/primes/dashboard/details') {
    const year =
      searchParams.get('year')?.trim() ??
      primesContext?.selectedYear ??
      (primesContext?.referenceDate ? format(primesContext.referenceDate, 'yyyy') : format(new Date(), 'yyyy'));

    crumbs.push({ label: translateMenu('breadcrumb.tamSynthesis'), to: '/primes/dashboard' });
    if (primesContext?.tamDisplayName) {
      crumbs.push({ label: primesContext.tamDisplayName });
    }
    crumbs.push({ label: year });
    return crumbs;
  }

  if (pathname === '/primes/annuel') {
    const year = primesContext?.selectedYear ?? format(new Date(), 'yyyy');
    crumbs.push({ label: year });
    return crumbs;
  }

  const primesSegment = pathname.slice('/primes/'.length);
  const isMonthlyRoute = primesSegment === 'mois' || isValidMonthYear(primesSegment);

  if (!isMonthlyRoute) {
    return null;
  }

  let referenceDate = primesContext?.referenceDate;
  if (!referenceDate && isValidMonthYear(primesSegment)) {
    referenceDate = parseMonthYearToDate(primesSegment);
  }
  if (!referenceDate) {
    referenceDate = getDefaultPrimeReferenceDate();
  }

  const year = format(referenceDate, 'yyyy');
  const monthLabel = formatMonthYearToMonthLabel(formatReferenceDateToMonthYear(referenceDate));
  const tamFromSearch = searchParams.get('tam')?.trim() ?? '';
  const isCLevelPreview = isCLevel && tamFromSearch !== '' && validateEmail(tamFromSearch);

  if (isCLevelPreview) {
    crumbs.push({ label: translateMenu('breadcrumb.tamSynthesis'), to: '/primes/dashboard' });
    if (primesContext?.tamDisplayName) {
      crumbs.push({ label: primesContext.tamDisplayName });
    }
    crumbs.push({
      label: year,
      to: buildCLevelAnnualDetailPath(tamFromSearch, year),
    });
    crumbs.push({ label: monthLabel });
    return crumbs;
  }

  crumbs.push({
    label: year,
    to: '/primes/annuel',
  });
  crumbs.push({ label: monthLabel });
  return crumbs;
};
